import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { buildVistaCursoAdminPath, VISTA_CURSO_RETURN_EDITOR } from "../../../../utils/vistaCursosAdminReturn";
import { getMessage } from "../../../../data/locales";
import { countTemarioStats } from "../../../../utils/temarioTree";
import { testMatchesTab } from "../../../../utils/testMatchesTab";
import TemarioContenidoPreview from "../../../../components/TemarioContenidoPreview";

export default function EditarCursoVistaTab({
  courseId,
  titulo,
  pestanasCurso,
  bloquesContenido,
  tests,
  onBeforeStudentPreview,
}) {
  const navigate = useNavigate();
  const pestanasNoVacias = useMemo(() => {
    const fromDeclared = (Array.isArray(pestanasCurso) ? pestanasCurso : [])
      .map((p) => (p || "").trim())
      .filter(Boolean);
    if (fromDeclared.length > 0) {
      return fromDeclared.map((label) => ({ key: label, label }));
    }
    return Object.keys(bloquesContenido || {})
      .map((rawKey) => {
        const label = (rawKey || "").trim();
        return label ? { key: rawKey, label } : null;
      })
      .filter(Boolean);
  }, [pestanasCurso, bloquesContenido]);
  const [pestanaActiva, setPestanaActiva] = useState("");

  useEffect(() => {
    setPestanaActiva((prev) => {
      const keys = pestanasNoVacias.map((entry) => entry.key);
      return prev && keys.includes(prev) ? prev : pestanasNoVacias[0]?.key ?? "";
    });
  }, [pestanasNoVacias]);

  const activeTabLabel = pestanasNoVacias.find((entry) => entry.key === pestanaActiva)?.label ?? pestanaActiva;

  const subsActiva = pestanaActiva ? bloquesContenido[pestanaActiva] || [] : [];
  const { nodeCount, archivoCount } = countTemarioStats(subsActiva);
  const testsActiva = pestanaActiva ? tests.filter((t) => testMatchesTab(t, pestanaActiva)) : [];

  return (
    <div className="contenedor-creartets cc-vista-general-wrap">
      <div className="cc-vista-general-header">
        <h1>Vista general</h1>
        <p className="cc-vista-general-sub">
          Resumen alineado con el temario del curso: mismas pestañas que verá el alumno, vista jerárquica del
          contenido y tests vinculados por pestaña. Usa la vista previa para comprobar el resultado final.
        </p>
      </div>

      <div className="cc-vista-general-preview-actions">
        <button
          type="button"
          className="cc-vista-general-preview-btn"
          onClick={() => {
            if (typeof onBeforeStudentPreview === "function" && !onBeforeStudentPreview()) {
              return;
            }
            navigate(buildVistaCursoAdminPath(courseId, VISTA_CURSO_RETURN_EDITOR));
          }}
        >
          {getMessage("courseEditor.openStudentPreview")}
        </button>
      </div>

      <div className="vista-general-container cc-vista-general-panel">
        <div className="cc-vista-general-datos">
          <h2 className="cc-vista-general-titulo">{titulo || "(Sin título)"}</h2>
          <p className="cc-vista-general-meta">
            <span>
              Pestañas: <strong>{pestanasNoVacias.length}</strong>
            </span>
            <span>
              Tests totales: <strong>{tests.length}</strong>
            </span>
          </p>
        </div>

        {pestanasNoVacias.length === 0 ? (
          <p className="cc-doc-preview-empty">
            Añade pestañas en «Datos principales» para ver la estructura por bloque.
          </p>
        ) : (
          <>
            <nav className="vista-cursos-admin-tabs cc-vista-general-tabs" aria-label="Pestañas del curso">
              {pestanasNoVacias.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  className={key === pestanaActiva ? "active" : ""}
                  onClick={() => setPestanaActiva(key)}
                >
                  {label}
                </button>
              ))}
            </nav>

            {pestanaActiva ? (
              <div className="cc-vista-general-tab-body">
                <h3 className="cc-vista-general-tab-title">{activeTabLabel}</h3>
                <p className="cc-vista-general-block-stats">
                  Nodos del temario: <strong>{nodeCount}</strong> — Archivos: <strong>{archivoCount}</strong> —
                  Tests en esta pestaña: <strong>{testsActiva.length}</strong>
                </p>

                {subsActiva.length > 0 ? (
                  <div className="cc-vista-general-temario-box academia-course-temario-surface">
                    <TemarioContenidoPreview nodes={subsActiva} />
                  </div>
                ) : (
                  <p className="cc-doc-preview-empty">No hay nodos de temario en esta pestaña.</p>
                )}

                <h4 className="cc-vista-general-tests-title">Tests en esta pestaña</h4>
                {testsActiva.length === 0 ? (
                  <p className="cc-doc-preview-empty">No hay tests asociados a esta pestaña.</p>
                ) : (
                  <ul className="cc-vista-general-tests-list">
                    {testsActiva.map((t) => (
                      <li key={`${pestanaActiva}-${t.titulo}-${(t.preguntas || []).length}`}>
                        <strong>{t.titulo}</strong>
                        <span className="cc-vista-general-test-meta">
                          {" "}
                          — {(t.preguntas || []).length} preguntas
                          {typeof t.tiempo === "number" && t.tiempo > 0 ? ` — ${t.tiempo} min` : ""}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

EditarCursoVistaTab.propTypes = {
  courseId: PropTypes.string.isRequired,
  titulo: PropTypes.string.isRequired,
  pestanasCurso: PropTypes.arrayOf(PropTypes.string).isRequired,
  bloquesContenido: PropTypes.objectOf(PropTypes.array).isRequired,
  tests: PropTypes.arrayOf(PropTypes.object).isRequired,
  onBeforeStudentPreview: PropTypes.func,
};
